import React from 'react';
import PropTypes from 'prop-types';
import { useApolloClient } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { Box, Button, Chip, Stack, Typography } from '@mui/material';
import { PictureAsPdf as PdfIcon } from '@mui/icons-material';
import { ReactComponent as Logo } from 'images/logo.svg';
import PremiumCard from 'components/shared/surfaces/PremiumCard';
import ContentRenderer from 'components/shared/content/ContentRenderer';
import { NUTRITION_PROGRAM_TEMP_URL } from 'api/graphql/nutrition';

// Marca de agua del logo OTRIX detrás del documento (como el triángulo del PDF de referencia):
// GRANDE — ocupa todo el documento — y difuminada. El contenedor recorta (overflow: hidden).
const watermarkStyles = {
  width: '125%',
  maxWidth: 'none',
  opacity: 0.07,
  filter: 'blur(1px)',
  position: 'absolute',
  left: '50%',
  top: '50%',
  transform: 'translate(-50%, -50%)',
  pointerEvents: 'none'
};

/**
 * Documento de marca OTRIX para un programa de recomendaciones (general/específico). El contenido se
 * escribe libre en el editor enriquecido y se RENDERIZA aquí dentro de un marco de marca: marca de
 * agua del logo, cabecera con mark + tipo, banda de título y el cuerpo con `ContentRenderer`.
 * Se usa igual en la vista del cliente y en la vista previa del admin (decisión del usuario).
 */
function ProgramDocument({
  title = '',
  type = 'general',
  content = '',
  programId = null,
  hasPdf = false
}) {
  const { t } = useTranslation(['nutrition']);
  const client = useApolloClient();

  // Se nombra el eje además del valor ("Alcance: General"): a secas, "General" se confundía con
  // "asignado a todos", que es otra cosa y vive en el chip de destino de la card.
  const typeLabel = `${t('contentScope', { ns: 'nutrition', defaultValue: 'Alcance del contenido' })}: ${
    type === 'specific'
      ? t('typeSpecific', { ns: 'nutrition', defaultValue: 'Específico' })
      : t('typeGeneral', { ns: 'nutrition', defaultValue: 'General' })
  }`;

  // El bucket es privado → se pide la URL presignada antes de abrir el PDF (no la URL cruda).
  const openPdf = async () => {
    if (!programId) return;
    const { data } = await client.query({
      query: NUTRITION_PROGRAM_TEMP_URL,
      variables: { id: programId },
      fetchPolicy: 'network-only'
    });
    const url = data?.nutritionProgramTemporaryUrl;
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <PremiumCard innerSx={{ position: 'relative', overflow: 'hidden', p: { xs: 2.5, md: 4 } }}>
      <Box sx={{ position: 'relative' }}>
        <Logo style={watermarkStyles} />

        <Box sx={{ position: 'relative' }}>
          {/* Cabecera de marca. El chip del alcance ya NO va aquí: al nombrar el eje completo
              ("Alcance del contenido: General") no cabía junto al logo en móvil y acababa
              montándose justo debajo de OTRIX. Ahora vive en la banda de título, que ocupa el
              ancho entero y es donde se entiende de qué documento habla. */}
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
            <Box sx={{ color: 'gold.main', display: 'flex', flexShrink: 0 }}>
              <Logo style={{ width: 30, height: 30 }} />
            </Box>
            <Typography variant="overline" sx={{ fontWeight: 800, letterSpacing: '0.18em', color: 'gold.main' }}>
              OTRIX
            </Typography>
          </Stack>

          {/* Banda de título */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            useFlexGap
            flexWrap="wrap"
            sx={{ rowGap: 0.75 }}>
            <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: '0.14em', fontWeight: 700 }}>
              {t('programDocEyebrow', { ns: 'nutrition', defaultValue: 'Plan de recomendaciones' })}
            </Typography>
            <Chip
              size="small"
              label={typeLabel}
              sx={{ backgroundColor: 'gold.soft', color: 'gold.main', fontWeight: 700, maxWidth: '100%' }}
            />
          </Stack>
          <Typography variant="h5" sx={{ fontWeight: 800, mb: 1, lineHeight: 1.2 }}>
            {title}
          </Typography>
          <Box sx={{ height: '2px', width: 56, backgroundColor: 'gold.main', borderRadius: 1, mb: 2.5 }} />

          {/* Cuerpo. En modo claro se refuerzan bordes/cabeceras de tabla para que tengan tanto
              contraste como en oscuro. */}
          {content
            ? (
              <ContentRenderer
                value={content}
                sx={{
                  '& th, & td': { border: '1px solid', borderColor: (th) => (th.palette.mode === 'light' ? 'rgba(0,0,0,0.32)' : 'hairline.main'), px: 1.25, py: 0.75, textAlign: 'left' },
                  '& th': { backgroundColor: (th) => (th.palette.mode === 'light' ? 'rgba(0,0,0,0.07)' : 'surface.overlay'), color: 'text.primary', fontWeight: 700 }
                }}
              />
            )
            : <Typography variant="body2" sx={{ color: 'text.disabled' }}>{t('programDocEmpty', { ns: 'nutrition', defaultValue: 'Sin contenido todavía.' })}</Typography>}

          {hasPdf && programId && (
            <Button
              onClick={openPdf}
              variant="outlined"
              color="inherit"
              startIcon={<PdfIcon />}
              sx={{ mt: 3, color: 'text.primary', borderColor: 'hairline.strong', textTransform: 'none', fontWeight: 700 }}
            >
              {t('viewPdf', { ns: 'nutrition', defaultValue: 'Ver PDF adjunto' })}
            </Button>
          )}
        </Box>
      </Box>
    </PremiumCard>
  );
}

ProgramDocument.propTypes = {
  title: PropTypes.string,
  type: PropTypes.string,
  content: PropTypes.string,
  programId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  hasPdf: PropTypes.bool
};

export default ProgramDocument;
